import { groupBy, mapValues, mean, sortBy } from 'es-toolkit';
import { get } from 'es-toolkit/compat';

import dayjs from '@skynet/formatting-date';
import { AssetType } from '@skynet/types-asset-type';

import type {
  JsonBalanceHistoryExtended,
  JsonBalanceHistoryItem,
  PriceHistoryItemType,
} from 'src/store/shared/balance-history/types';
import { historyItemTypeMap } from 'src/store/shared/balance-history/types';
import type { PricesList } from '@skynet/types-price-item';

import { getIsSmallBalance, getToken, isStableToken } from '../core/helpers';
import type { GetPricePerAssetParams, GetPricePerAssetSource } from '../core/types';

export const getPricePerAsset = <T extends GetPricePerAssetSource>({
  source,
  token,
  key,
  selection = [],
  isSmallBalancesVisible = false,
}: GetPricePerAssetParams<T>): number => {
  if (isStableToken(token)) {
    return 1;
  }

  const arrayOfPrice = source.reduce<number[]>((sum, item) => {
    if (selection?.length && !selection.includes(item.key)) {
      return sum;
    }

    const foundAsset = token ? item.assets.find((asset) => asset.token === token) : undefined;

    if (!foundAsset) {
      return sum;
    }

    if (item?.isTotalDoubling || foundAsset?.isDoubling) {
      return sum;
    }

    if (!isSmallBalancesVisible && foundAsset.isSmallBalance) {
      return sum;
    }

    const lastPrice = get(foundAsset ?? item, key) ?? 0;

    if (!lastPrice) {
      return sum;
    }

    return [...sum, lastPrice];
  }, []);

  // `mean([])` is NaN: when no counted account prices the token (e.g. the selection holds none of
  // it), report no price rather than poisoning every `volume * price` downstream.
  return arrayOfPrice.length > 0 ? mean(arrayOfPrice) : 0;
};

/** Parameters of {@link getTokenPrice}. */
export interface GetTokenPriceParams<T extends GetPricePerAssetSource> {
  token: string;
  /** Accounts to average the price over when the live quote is not used. */
  source: T[];
  /** Live quotes by token, as flattened by {@link getPrices} (`selectPrices`). */
  prices?: Record<string, PriceHistoryItemType[]>;
  selection?: GetPricePerAssetParams<T>['selection'];
  isSmallBalancesVisible?: boolean;
}

/**
 * USD price of `token` for a balances summary row (the table's Total, the list's Summary).
 *
 * The live quote is used for the whole book; once accounts are selected — or the feed has no
 * usable quote — the price is the average over the counted accounts instead
 * ({@link getPricePerAsset}), which is 0 when none of them prices the token.
 */
export const getTokenPrice = <T extends GetPricePerAssetSource>({
  token,
  source,
  prices,
  selection = [],
  isSmallBalancesVisible = false,
}: GetTokenPriceParams<T>): number => {
  const livePrice = prices?.[token]?.[0]?.price;

  if (selection.length === 0 && livePrice && !Number.isNaN(livePrice)) {
    return livePrice;
  }

  return getPricePerAsset({ source, key: 'lastPrice', token, selection, isSmallBalancesVisible });
};

export const findHistoryPrices = (
  history: JsonBalanceHistoryItem[],
  comparisonEndDate: string | undefined,
  selection: string[] = [],
) => {
  const filteredHistory = history.filter((item) => {
    const isSmallBalance = getIsSmallBalance({
      ...item,
      entryPrice: 0,
      lastPrice: item.lastPrice,
      volume: item.volume,
      volumeUSD: item.volume * item.lastPrice,
      leverage: 1,
      assetType: AssetType.Spot,
      positionType: null,
      unrealizedPnL: 0,
      marginBalance: 0,
    });

    if (selection.length && !selection.includes(item.accountID)) {
      return false;
    }

    // skip non base tokens and future dates (for midnight snapshot)
    return !isSmallBalance && item.type === 1 && dayjs(item.date).isSameOrBefore(comparisonEndDate);
  });

  // here might be different tokens to handle

  const historyMapPerToken = groupBy(filteredHistory, (item) => item.token);

  return mapValues(historyMapPerToken, (tokenHistory) => {
    const tokenHistoryMapPerDate = groupBy(tokenHistory, (item) => item.date);
    const tokenHistoryMap = Object.entries(tokenHistoryMapPerDate).map(([date, dateHistory]) => {
      const tokenPrice = dateHistory.reduce((sum, item) => sum + item.lastPrice, 0);
      const tokenQty = dateHistory.length;

      return {
        date,
        price: tokenPrice / tokenQty,
      };
    });

    return tokenHistoryMap;
  });
};

/**
 * Flattens the `date -> quotes[]` feed into a `token -> points[]` map.
 *
 * Dates are normalised to UTC: the feed sends UTC instants (with or without a trailing `Z`) and
 * every consumer of these points reads them back with `dayjs.utc`, so formatting them in the
 * viewer's local zone would shift each timestamp by the local offset.
 */
export const getPrices = (prices?: PricesList) => {
  const pricesMap: Record<string, PriceHistoryItemType[]> = {};
  if (prices != null && Object.keys(prices).length > 0) {
    Object.entries(prices).forEach(([date, items]) => {
      items.forEach((item) => {
        if (!pricesMap[item.token]) {
          pricesMap[item.token] = [];
        }
        pricesMap[item.token].push({
          price: item.price,
          date: dayjs.utc(date).format('YYYY-MM-DD HH:mm'),
        });
      });
    });
  }

  return pricesMap;
};

interface AliasedPriceSeries {
  /** Price points of the raw symbol backing this key. */
  points: PriceHistoryItemType[];
  /** The raw symbol whose series this key holds (a raw key backs itself). */
  rawToken: string;
}

/**
 * Keys a `token -> points[]` map by raw symbol and, additionally, by every mapped label.
 *
 * A label that already has a series of its own keeps it; when several raw symbols share one label,
 * the first of them wins. Each entry carries the raw symbol it came from, so a label's live quote
 * is later resolved against the very symbol that produced its series rather than another symbol
 * that happens to share the label.
 */
const withMappedTokenAliases = (
  byToken: Record<string, PriceHistoryItemType[]>,
  tokenMap: Record<string, string>,
): Record<string, AliasedPriceSeries> => {
  const result: Record<string, AliasedPriceSeries> = {};

  Object.entries(byToken).forEach(([token, points]) => {
    result[token] = { points, rawToken: token };
  });

  // Second pass: every raw series exists by now, so a label that is itself a quoted symbol is
  // never shadowed by the alias of another symbol.
  Object.entries(byToken).forEach(([token, points]) => {
    const label = getToken(token, tokenMap);
    if (!Object.hasOwn(result, label)) {
      result[label] = { points, rawToken: token };
    }
  });

  return result;
};

/** Latest quote of a live-price series, or 0 when the feed carries none. */
const getLatestQuote = (quotes: PriceHistoryItemType[] | undefined): number =>
  sortBy(quotes ?? [], ['date']).at(-1)?.price ?? 0;

/**
 * Builds the per-token price series (sorted by date) shown by the compare views.
 *
 * The server price feed is authoritative; prices averaged from balance snapshots only fill in
 * tokens the feed does not quote. Each series is also exposed under its `tokenMap` label, because
 * compare columns are keyed by label. For the `current` end snapshot every series ends on the live
 * quote from `currentPricesByToken` — the quote of the series key itself when the feed carries one
 * (labels are quoted too), otherwise the quote of the raw symbol that produced the series — or on
 * its last known price when neither is quoted.
 *
 * @param currentPricesByToken - Live quotes already flattened by {@link getPrices} (see
 * `selectPrices`), so the same normalised map is shared instead of recomputed per selector.
 */
export const getHistoryPrices = (
  history: JsonBalanceHistoryItem[],
  comparisonEndDate: string | undefined,
  selection: string[] | undefined = [],
  priceHistory: PricesList | undefined = {},
  endDateSnapshot: string | undefined = undefined,
  currentPricesByToken: Record<string, PriceHistoryItemType[]> | undefined = {},
  tokenMap: Record<string, string> = {},
): Record<string, PriceHistoryItemType[]> => {
  // The feed entry replaces the snapshot entry for the same key, carrying both its points and the
  // raw symbol they came from — the two can never drift apart into different merge orders.
  const merged: Record<string, AliasedPriceSeries> = {
    ...withMappedTokenAliases(findHistoryPrices(history, comparisonEndDate, selection), tokenMap),
    ...withMappedTokenAliases(getPrices(priceHistory), tokenMap),
  };

  const isCurrentSnapshot = endDateSnapshot === 'current';
  // The live point is dated in UTC like every other point of the series: the compare views render
  // it with SkyDateTimeUTC and getCompareEndPrice reads it with dayjs.utc, so local digits showed
  // shifted by the viewer's UTC offset. It is flagged `isCurrent` so getNewHistoryDataItem pairs a
  // price-less live balance (history.ts buildCurrentSnapshot) with it regardless of either date
  // (SKY-4575).
  const now = dayjs.utc().format('YYYY-MM-DD HH:mm');

  return mapValues(merged, ({ points, rawToken }, key) => {
    // `sortBy` copies, so appending below never leaks into the series of the alias sharing
    // `points` — nor back into the snapshot / feed maps.
    const series = sortBy(points, ['date']);
    const lastItem = series.at(-1);

    if (!isCurrentSnapshot || !lastItem) {
      return series;
    }

    // A zero quote means the feed has no price for that key, not that the token is worthless, so
    // a zero-priced label quote still falls through to the raw symbol the label aliases.
    const liveQuote =
      getLatestQuote(currentPricesByToken[key]) ||
      (rawToken === key ? 0 : getLatestQuote(currentPricesByToken[rawToken]));

    return [...series, { date: now, price: liveQuote || lastItem.price, isCurrent: true }];
  });
};

/**
 * First non-zero balance-snapshot price of a base token, used only as a last-resort fallback.
 *
 * Scans `array` in list order and returns the first account whose price for the token isn't
 * zero — it does not sort by date, so despite the name this is not necessarily the *freshest*
 * price, only the first available one. `array` is the extended history, whose `assets[].token` is
 * already the mapped label (see `getExtendedBalancesHistory`), so a compare column's label matches
 * directly. Without `token` the first base asset of the first account that holds a non-zero price
 * wins — kept for legacy callers.
 */
export const getFallbackHistoryPrices = (array: JsonBalanceHistoryExtended[], token?: string) => {
  let result = 0;
  for (const item of array) {
    const asset = token
      ? item.assets.find((a) => a.token === token && a.type === historyItemTypeMap[1])
      : item.assets.find((a) => a.type === historyItemTypeMap[1]);
    const { lastPrice = 0 } = asset ?? {};
    result = lastPrice;
    if (result) break;
  }
  return result;
};

/**
 * End price of a base token shown in the compare table's column header.
 *
 * {@link getHistoryPrices} already ends every series on the live quote for a `current` end
 * snapshot, so the series normally carries the price. When it does not — the feed quotes neither
 * the column's label nor the raw symbol behind it — fall back to {@link getFallbackHistoryPrices}
 * (the first non-zero balance-snapshot price, in account-list order). The fallback applies only
 * while the series ends today: for an older end snapshot, or when there is no series at all, a
 * current balance price would silently replace a historical (or absent) one, so those cases
 * return 0 instead.
 *
 * The compare list's summary card does *not* use this: it shares its base-token price with
 * `getCompareSummary`'s own per-account average instead, so both stay consistent with the
 * averaged volume / net-change cells shown alongside it (see `CompareListItem`).
 */
export const getCompareEndPrice = (
  series: PriceHistoryItemType[] | undefined,
  data: JsonBalanceHistoryExtended[],
  token: string,
): number => {
  const lastItem = series?.at(-1);

  if (lastItem?.price) {
    return lastItem.price;
  }

  if (!lastItem) {
    return 0;
  }

  // Both sides must be resolved in UTC: `lastItem.date` is a zone-less wall-clock string that
  // getPrices/getHistoryPrices already normalised to UTC, so parsing it with plain `dayjs()` would
  // re-interpret it in the viewer's local zone and shift the day boundary by their UTC offset.
  return dayjs.utc().isSame(dayjs.utc(lastItem.date), 'day')
    ? getFallbackHistoryPrices(data, token)
    : 0;
};
